define('core/models/Thread',[
    'underscore',
    'backbone',
    'loglevel',

    'core/config/urls',

    'core/utils',
    'core/api',
    'core/config',
    'core/advice',
    'core/UniqueModel',
    'core/utils/threadRatingsHelpers',

    'core/models/User',
], function (
    _,
    Backbone,
    logger,

    urls,

    utils,
    api,
    config,
    advice,
    UniqueModel,
    threadRatingsHelpers,

    User
) {
    'use strict';

    var parent = Backbone.Model;
    var parentProto = parent.prototype;


    var Thread = parent.extend({
        defaults: {
            author: null,
            category: null,
            createdAt: null,  // Date time in string. Format: YYYY-MM-DDTHH:mm:ss
            forum: null,
            identifiers: [],
            ipAddress: null,
            isClosed: false,
            isDeleted: false,
            hasStreaming: false,
            link: null,  // Direct url to this thread
            message: null,
            slug: null,
            title: null,
            userSubscription: false,

            // Counters
            posts: 0,
            likes: 0,
            dislikes: 0,
            userScore: 0,
        },

        initialize: function (_attributes, options) {
            options = options || {};

            // Array of moderator ids for this thread (integers)
            this.moderators = options.moderators;

            this.forum = options.forum;

            this.on('change:userScore', function () {
                var userScore = this.get('userScore');

                // If the user voted this Thread but likes is 0, means we got a
                // cached result for likes, so update it to not confuse the user
                if (userScore > 0 && this.get('likes') === 0)
                    this.set('likes', userScore);

                // NOTE: Note that we don't handle dislikes and negative user
                //       votes above since we don't have that concept yet.
            }, this);
        },

        _vote: function (vote, score) {
            var delta = vote - score;

            // If the vote is unchanged, ignore it.
            if (delta === 0)
                return delta;

            this.set('likes', this.get('likes') + delta);
            return delta;
        },

        vote: function (vote) {
            var self = this;
            var delta = self._vote(vote, self.get('userScore'));

            if (delta === 0)
                return;

            this.set('userScore', vote);

            api.call('threads/vote.json', {
                data: {
                    thread: this.id,
                    vote: vote,
                },
                method: 'POST',
                success: function (data) {
                    if (!data.response.id)
                        return;

                    self.trigger('vote:success', data);
                },
            });
        },

        fetch: function (options) {
            var self = this;
            var attrs = self.attributes;
            var query;

            options = options || {};

            if (attrs.identifier)
                query = 'ident:' + attrs.identifier;
            else
                query = 'link:' + attrs.url;

            api.call('threads/details.json', {
                data: {
                    thread: query,
                    forum: attrs.forum,
                },

                success: function (data) {
                    self.set(data.response);
                    if (options.success) options.success();
                },

                error: function () {
                    if (config.debug)  // Create a new thread
                        self.save({}, { success: options.success });
                    else  // For Alpha release only.
                        logger.info('Couldn\'t find thread; not creating in production.');
                },
            });
        },

        _toggleState: function (isOpen, options) {
            if (!options)
                options = {};

            var action = isOpen ? 'open.json' : 'close.json';
            this.set('isClosed', !isOpen);

            return api.call('threads/' + action, {
                method: 'POST',
                data: {
                    thread: this.id,
                },
                success: options.success,
                error: options.error,
            });
        },

        open: function (options) {
            return this._toggleState(true, options);
        },

        close: function (options) {
            return this._toggleState(false, options);
        },

        /**
         * Change the thread's premoderation status. Auth'd user must have write
         * privileges for the thread.
         *
         * @param {boolean} isPremoderated - the new premoderation status
         * @param {Object} [options] - options to pass to the api call
         * @returns {jqXHR}
         */
        premoderate: function (isPremoderated, options) {
            this.set('validateAllPosts', isPremoderated);

            return api.call('threads/update', _.extend({}, options, {
                method: 'POST',
                data: _.extend({
                    thread: this.id,
                    validateAllPosts: isPremoderated ? 1 : 0,
                }, options && options.data),
            }));
        },

        sync: function () {
            var self = this;
            var attrs = self.attributes;

            api.call('threads/create.json', {
                data: {
                    title: attrs.title,
                    forum: attrs.forum,
                    identifier: attrs.identifier,
                    url: attrs.url,
                },
                method: 'POST',
                success: function (data) {
                    self.set(data.response);
                },
            });
        },

        fetchRatings: function () {
            var self = this;

            var data = { thread: self.id };

            return api.call('threads/ratingsSummary.json', {
                data: data,
                method: 'GET',
                success: function (response) {
                    self.set('ratings', response.response);
                },
            });
        },

        toggleRatingsEnabled: function () {
            var self = this;
            if (!threadRatingsHelpers.isForumModelRatingsEnabled(self.forum))
                return;

            var ratingsEnabled = self.get('ratingsEnabled');

            var data = {
                thread: self.id,
                ratingsEnabled: ratingsEnabled ? 0 : 1, // invert the ratings enabled
            };

            self.set('ratingsEnabled', !ratingsEnabled);

            return api.call('threads/update.json', {
                data: data,
                method: 'POST',
            });
        },

        /**
         * Increment the reported count of posts for this thread.
         *
         * You cannot decrement the post count below 0.
         * @param  {number} inc A positive or negative number
         */
        incrementPostCount: function (inc) {
            var count = this.get('posts') + inc;
            this.set('posts', count > 0 ? count : 0);
        },

        isModerator: function (user) {
            // Accepts user instance or user id and returns true, false,
            // or undefined (if function cannot determine)
            var id;

            if (!this.moderators)
                return;

            id = user instanceof User || _.isObject(user) ? user.id : user;
            id = parseInt(id, 10);
            return _(this.moderators).contains(id);
        },

        subscribe: function (subscribe) {
            subscribe = subscribe !== false;  // default is to subscribe

            // the below can be a boolean or the e-mail used to subscribe
            var currentSubscription = this.get('userSubscription');
            if (currentSubscription === subscribe)
                return;  // do nothing if the state is same

            this.set('userSubscription', subscribe);

            var endpoint = subscribe ? 'subscribe.json' : 'unsubscribe.json';
            var data = { thread: this.id };

            return api.call('threads/' + endpoint, {
                data: data,
                method: 'POST',
            });
        },

        twitterText: function (url) {
            var charsRemaining = 140 - (url.length + 1);  // +1 for space before url
            var text = this.get('clean_title');
            text = utils.niceTruncate(text, charsRemaining);
            return text;
        },

        permalink: function () {
            // Sometimes url or link isn't available. In which case, use the "current
            // URL", which is the referring URL that opened the current embed context
            return this.get('url') || this.get('link') || this.currentUrl;
        },

        shortLink: function () {
            return urls.shortener + '/t/' + Number(this.id).toString(36);
        },

        toJSON: function () {
            var json = parentProto.toJSON.call(this);

            json.permalink = this.permalink();
            json.shortLink = this.shortLink();

            return json;
        },

        /**
         * Returns a full path to the discussion page on home for this thread.
         *
         * If channel is given, or if this thread is on the primary forum
         * for a channel, the discussion page will contain a link to the
         * channel.
         *
         * @param {Channel} [channel] - the channel the discussion page is to link to
         * @returns {string}
         */
        getDiscussionRoute: function (channel) {
            var route = [
                '',  // for leading slash
                'home',
                'discussion',
                this.forum.id,
                this.get('slug'),
                '',  // for trailing slash
            ];

            channel = channel || this.forum.channel;
            if (channel) {
                channel = channel.attributes || channel;
                route.splice(2, 0, 'channel', channel.slug);
            }

            return route.join('/');
        },
    });

    advice.withAdvice.call(Thread.prototype);


    Thread.withThreadVoteCollection = function (ThreadVoteCollection) {
        this.after('initialize', function () {
            this.votes = new ThreadVoteCollection();

            this.on('vote:success', function (data) {
                if (this.votes.get(data.response.id))
                    return;

                this.votes.add({
                    id: data.response.id,
                    score: data.response.vote, // TODO

                    // mark this vote as made by current user so we can ignore
                    // it when it comes through realtime
                    currentUser: true,
                });

            }, this);
        });
    };

    Thread.withPostCollection = function (PostCollection) {
        this.after('initialize', function (options) {
            options = options || {};
            this.posts = new PostCollection(options.posts, {
                thread: this,
                cursor: options.postCursor,
                order: options.order,
                perPage: this.postsPerPage,
            });

            this.listenTo(this.posts, 'add reset', function (posts) {
                // Reset returns Collection object, so can't use
                posts = posts.models ? posts.models : [posts];

                if (this.users) {
                    _.each(posts, function (post) {
                        if (!this.users.get(post.author.id))
                            this.users.add(post.author);
                    });
                }

                this.recalculatePostCount();
            });

            this.listenTo(
                this.posts,
                'change:isDeleted change:isFlagged',
                function (_post, value) {
                    // If isDeleted or isFlagged is true then we decrement the post count.
                    if (value)
                        this.incrementPostCount(-1);
                }
            );
        });

        /**
         * Recalculate `posts` attribute of this thread.
         *
         * If the current reported post count is 50 or less then we manually count how many
         * of the posts we currently have on the page are publicly visible.
         * If the API reported more than 50 then we don't care about accuracy as much.
         */
        this.recalculatePostCount = function () {
            var count = this.get('posts');
            if (count > 50)
                return;

            // We want to count _all_ the posts that we have, not
            // just the posts that we're displaying.
            count = this.posts.reduce(function (memo, post) {
                return post.isPublic() ? memo + 1 : memo;
            }, 0);
            this.set('posts', count);
        };
    };

    UniqueModel.addType('Thread', Thread);

    return Thread;
});

